import { type ReactNode } from 'react';
import styles from './timeline-divider.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

type TimelineDividerProps = {
  /** The phase this divider opens ("The day before", "On the day"). */
  label: ReactNode;
  /** Optional muted trailing detail — a duration, a count, a hint. */
  detail?: ReactNode;
  /** Heading level rendered for `label` (default h3). */
  as?: 'h2' | 'h3' | 'h4';
  /** Marks the divider as the current phase — accent rule and ink. */
  active?: boolean;
  id?: string;
  className?: string;
};

/**
 * A labelled rule that cuts an ordered sequence into named phases.
 *
 * Lighter than {@link SectionHeader}: that one opens a page section in display
 * type, this one sits INSIDE a list and separates it, so the label stays at
 * eyebrow weight and the rule carries the structure. Used by the step list to
 * split day-ahead prep from on-the-day cooking (#919); anything else that
 * phases a sequence composes it rather than redrawing the treatment.
 */
export function TimelineDivider({
  label,
  detail,
  as: Heading = 'h3',
  active = false,
  id,
  className,
}: TimelineDividerProps) {
  return (
    <div
      className={cx(styles.divider, active && styles.active, className)}
      data-active={active ? 'true' : undefined}
    >
      <Heading id={id} className={styles.label}>
        {label}
      </Heading>
      {detail ? <span className={styles.detail}>{detail}</span> : null}
      <span className={styles.rule} aria-hidden="true" />
    </div>
  );
}
